'use client';

import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  useSyncExternalStore,
  type PointerEvent,
  type RefObject,
} from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import dynamic from 'next/dynamic';
import { ArrowLeft, ArrowRight, ArrowClockwise, X } from '@phosphor-icons/react';
import {
  AlertCircleIcon,
  CheckCircleIcon,
  CheckIcon,
  ClockIcon,
  CrosshairIcon,
  MapIcon,
  MapPinIcon,
  PhoneIcon,
} from '@/components/icons';
import { useAddressStore, type SelectedAddress } from '@/stores/address-store';
import { deliveryApi, type City } from '@/lib/api/delivery';
import { ymAddressSelected, tmrAddressSelected } from '@/lib/analytics';
import { deliveryTimeLabel } from '@/components/delivery/delivery-model';
import { AddressInput } from './address-input';
import { createSheetViewportTracker } from './sheet-viewport';
import type { YandexMapHandle } from './yandex-map';
import './address-sheet.css';

const YandexMap = dynamic(() => import('./yandex-map').then((mod) => mod.YandexMap), {
  ssr: false,
  loading: () => (
    <div className="address-map-loading" role="status">
      <MapIcon size={32} />
      <span>Загружаем карту…</span>
    </div>
  ),
});

const mobileQuery = '(max-width: 767px)';
function subscribeMobile(callback: () => void) {
  const media = window.matchMedia(mobileQuery);
  media.addEventListener('change', callback);
  return () => media.removeEventListener('change', callback);
}
const getMobile = () => window.matchMedia(mobileQuery).matches;
const serverMobile = () => false;
type DialogActions = { cancel: () => void };

function DeliveryTerms({ address }: { address: SelectedAddress }) {
  return (
    <div className="address-result address-result-success">
      <div className="address-result-heading">
        <CheckCircleIcon size={23} weight="fill" />
        <h3>Да, сюда доставляем</h3>
      </div>
      <p className="address-result-address">{address.address}</p>
      <dl className="address-terms">
        <div>
          <dt>Доставка</dt>
          <dd>
            {address.deliveryCost == null
              ? 'Уточним'
              : address.deliveryCost === 0
                ? 'Бесплатно'
                : `${address.deliveryCost} ₽`}
          </dd>
        </div>
        <div>
          <dt>Время</dt>
          <dd>{address.estimatedTime ? deliveryTimeLabel(address.estimatedTime) : 'Уточним'}</dd>
        </div>
        <div>
          <dt>Заказ от</dt>
          <dd>{address.minOrderSum == null ? 'Уточним' : `${address.minOrderSum} ₽`}</dd>
        </div>
      </dl>
    </div>
  );
}

function AddressDialogContent({ actionsRef }: { actionsRef: RefObject<DialogActions | null> }) {
  const {
    selectedCity,
    selectedAddress,
    cities,
    isLoading,
    addressCheckError,
    fetchCities,
    fetchDeliveryPoints,
    setSelectedCity,
    setSelectedAddress,
    checkAddress,
    clearAddressCheckError,
    confirmSheet,
    closeSheet,
  } = useAddressStore();
  const [text, setText] = useState(selectedAddress?.address || '');
  const [mapMode, setMapMode] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [warning, setWarning] = useState<string | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const mobile = useSyncExternalStore(subscribeMobile, getMobile, serverMobile);
  const dialogRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<YandexMapHandle>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const initial = useRef({ address: selectedAddress, city: selectedCity });
  const requestRef = useRef(0);
  const pointerRef = useRef<{ x: number; y: number } | null>(null);
  const mounted = useRef(true);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const showMap = !mobile || mapMode;

  useEffect(() => {
    const sequence = requestRef;
    mounted.current = true;
    clearAddressCheckError();
    return () => {
      mounted.current = false;
      ++sequence.current;
    };
  }, [clearAddressCheckError]);
  useEffect(() => {
    if (!cities.length) void fetchCities();
  }, [cities.length, fetchCities]);
  useEffect(() => {
    if (selectedCity && !useAddressStore.getState().deliveryPoints.length)
      void fetchDeliveryPoints(selectedCity.id);
  }, [selectedCity, fetchDeliveryPoints]);

  // Resize the sheet to the visible viewport, including the mobile keyboard.
  // CSS variables avoid React work on every VisualViewport scroll/resize event.
  useEffect(() => {
    if (!mobile || !window.visualViewport) return;
    const viewport = window.visualViewport;
    const track = createSheetViewportTracker();
    let frame = 0;
    const update = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const element = dialogRef.current;
        if (!element) return;
        const state = track(viewport.width, viewport.height, window.innerHeight);
        element.style.setProperty('--address-vh', `${viewport.height}px`);
        element.style.setProperty('--address-vtop', `${viewport.offsetTop}px`);
        element.dataset.keyboard = String(state.keyboard);
        element.dataset.short = String(state.short);
      });
    };
    update();
    viewport.addEventListener('resize', update);
    viewport.addEventListener('scroll', update);
    return () => {
      cancelAnimationFrame(frame);
      viewport.removeEventListener('resize', update);
      viewport.removeEventListener('scroll', update);
    };
  }, [mobile]);

  const cancel = useCallback(() => {
    ++requestRef.current;
    const current = useAddressStore.getState().selectedAddress;
    const previous = initial.current;
    const restore =
      !current?.isInZone && (previous.address?.isInZone || (!current && previous.address));
    if (restore && previous.city?.id !== useAddressStore.getState().selectedCity?.id)
      setSelectedCity(previous.city);
    // Preserve the established partial-cancel contract; always invalidate a pending check.
    setSelectedAddress(restore ? previous.address : current);
    clearAddressCheckError();
    closeSheet();
  }, [setSelectedAddress, setSelectedCity, clearAddressCheckError, closeSheet]);
  useImperativeHandle(actionsRef, () => ({ cancel }), [cancel]);

  const changeText = useCallback(
    (value: string) => {
      ++requestRef.current;
      setGeocoding(false);
      setText(value);
      setWarning(null);
      clearAddressCheckError();
      const current = useAddressStore.getState();
      if (!value.trim()) setSelectedAddress(null);
      else if (current.isLoading) setSelectedAddress(current.selectedAddress);
    },
    [setSelectedAddress, clearAddressCheckError]
  );

  const changeCity = (city: City) => {
    ++requestRef.current;
    setText('');
    setWarning(null);
    setGeocoding(false);
    setMapMode(false);
    setSelectedCity(city);
  };
  const matching = !!text.trim() && selectedAddress?.address.trim() === text.trim();
  const busy = isLoading || geocoding;
  const canConfirm =
    matching && selectedAddress?.isInZone === true && !busy && !warning && !addressCheckError;
  const confirm = () => {
    const store = useAddressStore.getState();
    if (
      !canConfirm ||
      store.isLoading ||
      store.addressCheckError ||
      store.selectedAddress?.address.trim() !== text.trim()
    )
      return;
    ++requestRef.current;
    ymAddressSelected();
    tmrAddressSelected();
    confirmSheet();
  };
  const retry = () => {
    if (text.trim())
      void checkAddress(text.trim(), matching ? selectedAddress?.coordinates : undefined);
  };

  const pick = async (event: PointerEvent<HTMLDivElement>) => {
    const down = pointerRef.current;
    pointerRef.current = null;
    if (
      !mapMode ||
      geocoding ||
      !mapReady ||
      !down ||
      Math.hypot(event.clientX - down.x, event.clientY - down.y) > 5
    )
      return;
    // SDK controls are inside the map container; zooming must never choose an address.
    const target = event.target as HTMLElement;
    if (target.closest('button, a, [role="button"], [class*="controls"], [class*="control__"]'))
      return;
    const geo = mapRef.current?.pixelToGeo(event.clientX, event.clientY);
    if (!geo) return;
    const request = ++requestRef.current;
    const cityId = selectedCity?.id;
    const isCurrent = () =>
      mounted.current &&
      requestRef.current === request &&
      useAddressStore.getState().selectedCity?.id === cityId;
    setSelectedAddress(useAddressStore.getState().selectedAddress);
    const initialSelection = useAddressStore.getState().selectedAddress;
    setGeocoding(true);
    setWarning(null);
    clearAddressCheckError();
    mapRef.current?.placeMarker(geo.lat, geo.lon);
    try {
      const result = await deliveryApi.reverseGeocode(geo.lat, geo.lon);
      if (!isCurrent() || useAddressStore.getState().selectedAddress !== initialSelection) return;
      const address = result.address || '';
      setText(address);
      if (!address || !result.data?.house) {
        setSelectedAddress(null);
        setWarning('Нужен номер дома');
        return;
      }
      setSelectedAddress({ address, coordinates: geo, isInZone: false });
      const checked = await checkAddress(address, geo);
      const current = useAddressStore.getState().selectedAddress;
      if (isCurrent() && !checked.error && current?.address === address)
        setSelectedAddress({ ...current, coordinates: geo });
    } catch {
      if (!isCurrent()) return;
      setWarning('Не удалось определить адрес');
      void import('@/lib/source-tracker')
        .then(({ captureEvent }) =>
          captureEvent('address_check_failed', {
            reason: 'reverse_geocode_failed',
            http_status: null,
            query: `${geo.lat},${geo.lon}`,
            has_prev_address: !!useAddressStore.getState().selectedAddress,
          })
        )
        .catch(() => {});
    } finally {
      if (isCurrent()) setGeocoding(false);
    }
  };

  const status = busy ? (
    <div className="address-status address-status-loading" role="status">
      <span className="address-spinner" />
      <div>
        <h3>{geocoding ? 'Определяем адрес' : 'Проверяем доставку'}</h3>
        <p>Это займёт несколько секунд</p>
      </div>
    </div>
  ) : addressCheckError ? (
    <div className="address-status address-status-warning" role="status">
      <AlertCircleIcon size={24} />
      <div>
        <h3>Не удалось проверить адрес</h3>
        <p>Сервис временно не ответил. Это не означает, что адрес вне зоны доставки.</p>
        <button className="address-inline-button" type="button" onClick={retry}>
          <ArrowClockwise size={18} />
          Повторить проверку
        </button>
      </div>
    </div>
  ) : warning ? (
    <div className="address-status address-status-warning" role="status">
      <MapPinIcon size={24} />
      <div>
        <h3>{warning}</h3>
        <p>Уточните улицу и дом в поиске или выберите точку на карте.</p>
        {mapMode && (
          <button className="address-inline-button" type="button" onClick={() => setMapMode(false)}>
            <ArrowLeft size={18} />К поиску адреса
          </button>
        )}
      </div>
    </div>
  ) : matching && selectedAddress ? (
    selectedAddress.isInZone ? (
      <DeliveryTerms address={selectedAddress} />
    ) : (
      <div className="address-status address-status-warning" role="status">
        <MapPinIcon size={24} />
        <div>
          <h3>Сюда пока не доставляем</h3>
          <p>
            Попробуйте другой адрес. Если вы рядом с границей зоны, позвоните — поможем уточнить.
          </p>
          <a className="address-inline-button" href="tel:+78633335050">
            <PhoneIcon size={18} />8 (863) 333-50-50
          </a>
        </div>
      </div>
    )
  ) : (
    <div className="address-empty">
      <div className="address-empty-symbol">
        <MapPinIcon size={28} weight="duotone" />
      </div>
      <h3>Начнём с вашего адреса</h3>
      <p>Покажем стоимость, время доставки и минимальную сумму заказа для вашего дома.</p>
      <div className="address-empty-note">
        <ClockIcon size={17} />
        <span>Условия зависят от зоны доставки</span>
      </div>
    </div>
  );

  return (
    <Dialog.Content
      ref={dialogRef}
      className={`address-dialog${mapMode ? ' address-dialog-map-mode' : ''}`}
      onOpenAutoFocus={(event) => {
        event.preventDefault();
        openerRef.current =
          document.activeElement instanceof HTMLElement ? document.activeElement : null;
        if (getMobile()) titleRef.current?.focus();
        else dialogRef.current?.querySelector<HTMLInputElement>('input[role="combobox"]')?.focus();
      }}
      onCloseAutoFocus={(event) => {
        event.preventDefault();
        if (openerRef.current?.isConnected) openerRef.current.focus({ preventScroll: true });
      }}
      onEscapeKeyDown={(event) => {
        const target = event.target as HTMLElement;
        if (
          target.getAttribute('role') === 'combobox' &&
          target.getAttribute('aria-expanded') === 'true'
        )
          event.preventDefault();
      }}
    >
      <div className="address-panel">
        <header className="address-header">
          <button
            className="address-close"
            type="button"
            onClick={cancel}
            aria-label="Закрыть выбор адреса"
          >
            <X size={23} />
          </button>
          <Dialog.Title ref={titleRef} tabIndex={-1}>
            Куда привезти?
          </Dialog.Title>
          <Dialog.Description>Проверим зону, стоимость и время доставки.</Dialog.Description>
          <div className="address-city">
            <MapPinIcon size={17} weight="fill" />
            {cities.length > 1 ? (
              <select
                aria-label="Город доставки"
                value={selectedCity?.id ?? ''}
                onChange={(event) => {
                  const city = cities.find((item) => item.id === Number(event.target.value));
                  if (city) changeCity(city);
                }}
              >
                <option value="" disabled>
                  Выберите город
                </option>
                {cities.map((city) => (
                  <option key={city.id} value={city.id}>
                    {city.name}
                  </option>
                ))}
              </select>
            ) : (
              <span>{selectedCity?.name || 'Загружаем город…'}</span>
            )}
          </div>
        </header>
        <div className="address-body">
          <div className="address-search-area">
            <AddressInput
              id="delivery-address-search"
              label="Улица и дом"
              value={text}
              onChange={changeText}
              onSelect={(suggestion) => {
                if (!suggestion.data.house) setWarning('Нужен номер дома');
              }}
            />
            <button
              className="address-map-toggle"
              type="button"
              aria-pressed={mapMode}
              onClick={() => {
                setMapMode(!mapMode);
                if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
              }}
            >
              <CrosshairIcon size={20} />
              <span>{mapMode ? 'Отменить выбор на карте' : 'Выбрать на карте'}</span>
              <ArrowRight size={18} />
            </button>
          </div>
          <div className="address-feedback" aria-live="polite" aria-atomic="true">
            {status}
          </div>
        </div>
        <footer className="address-action">
          <button
            className="address-confirm"
            type="button"
            disabled={!canConfirm}
            onClick={confirm}
          >
            <span>{busy ? 'Проверяем адрес…' : 'Доставить сюда'}</span>
            {canConfirm ? <CheckIcon size={21} weight="bold" /> : <ArrowRight size={21} />}
          </button>
          <p>
            {canConfirm
              ? 'Квартиру и подъезд уточним при оформлении'
              : 'Выберите точный адрес с номером дома'}
          </p>
        </footer>
      </div>
      {showMap && (
        <section className="address-map-panel" aria-label="Карта доставки">
          {mobile && mapMode && (
            <div className="address-map-top">
              <button className="address-map-back" type="button" onClick={() => setMapMode(false)}>
                <ArrowLeft size={18} />К поиску
              </button>
            </div>
          )}
          <div
            className={`address-map-canvas${mapMode ? ' is-picking' : ''}`}
            onPointerDown={(event) => {
              pointerRef.current = { x: event.clientX, y: event.clientY };
            }}
            onPointerCancel={() => {
              pointerRef.current = null;
            }}
            onPointerUp={pick}
          >
            <YandexMap
              ref={mapRef}
              coordinates={selectedAddress?.coordinates}
              zoom={16}
              showZones
              className="address-map-instance"
              onReady={() => setMapReady(true)}
              onError={() => setMapReady(false)}
            />
          </div>
        </section>
      )}
    </Dialog.Content>
  );
}

export function AddressSheet() {
  const open = useAddressStore((state) => state.isSheetOpen);
  const closeSheet = useAddressStore((state) => state.closeSheet);
  const actionsRef = useRef<DialogActions | null>(null);
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(value) => {
        if (!value) {
          if (actionsRef.current) actionsRef.current.cancel();
          else closeSheet();
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="address-dialog-overlay" />
        <AddressDialogContent actionsRef={actionsRef} />
      </Dialog.Portal>
    </Dialog.Root>
  );
}
